import React, { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { FormattedMessage } from "react-intl";

import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";

import { CafsTreeNodeType } from "../../../types/cafs-tree-node-type";
import { useDataSets } from "../../dashboard-view/api/useDatasets";
import { useCafsList } from "../api/useCafsList";
import { useCreateSubstitution } from "../api/useCreateSubstitution";

type FormValues = {
  DatasetId: number | null;
  SubstitutionType: "tree" | "crop" | "input" | null;
  OriginalInputId: number | null;
  OriginalPlantId: number | null;
  ReplacementInputId: number | null;
  ReplacementPlantId: number | null;
};

type Props = {
  open: boolean;
  onClose: () => void;
};

// Helper function to flatten hierarchical options for autocomplete
const flattenHierarchicalOptions = (
  nodes: CafsTreeNodeType[],
  level: number = 0,
  parentPath: string = "",
): Array<{
  id: number | string;
  label: string;
  name: string;
  uniqueKey: string;
}> => {
  const options: Array<{
    id: number | string;
    label: string;
    name: string;
    uniqueKey: string;
  }> = [];

  nodes.forEach((node, index) => {
    const indent = "\u00A0".repeat(level);
    const nodeName = `[${node.id}] ${node.name} ` || "Unnamed";
    const label = `${indent}${level === 0 ? "" : "\u221F "}${nodeName}`;
    const uniqueKey = `${parentPath}-${index}-${node.id}`;

    options.push({
      id: node.id,
      label: label,
      name: nodeName,
      uniqueKey: uniqueKey,
    });

    if (node.childNodes && node.childNodes.length > 0) {
      options.push(
        ...flattenHierarchicalOptions(node.childNodes, level + 1, uniqueKey),
      );
    }
  });

  return options;
};

export function CreateSubstitutionModal({ open, onClose }: Props) {
  const { datasets } = useDataSets();
  const { control, handleSubmit, reset, watch } = useForm<FormValues>({
    defaultValues: {
      DatasetId: null,
      SubstitutionType: "tree",
      OriginalInputId: null,
      OriginalPlantId: null,
      ReplacementInputId: null,
      ReplacementPlantId: null,
    },
  });

  const datasetOptions = useMemo(
    () =>
      Array.isArray(datasets?.Datasets)
        ? datasets?.Datasets.map(d => ({ id: d.Id!, label: d.Name! }))
        : [],
    [datasets],
  );

  const selectedDatasetId = watch("DatasetId");
  const selectedType = watch("SubstitutionType");
  const { trees, crops, inputs } = useCafsList(selectedDatasetId || undefined);
  const { createSubstitution, isPending } = useCreateSubstitution(onClose);

  // Flatten hierarchical options for autocomplete
  const treeOptions = useMemo(() => flattenHierarchicalOptions(trees), [trees]);

  const cropOptions = useMemo(() => flattenHierarchicalOptions(crops), [crops]);

  const inputOptions = useMemo(
    () => flattenHierarchicalOptions(inputs),
    [inputs],
  );

  // Get the appropriate options based on selected type
  const currentOptions = useMemo(() => {
    switch (selectedType) {
      case "tree":
        return treeOptions;
      case "crop":
        return cropOptions;
      case "input":
        return inputOptions;
      default:
        return [];
    }
  }, [selectedType, treeOptions, cropOptions, inputOptions]);

  const onSubmit = (values: FormValues) => {
    createSubstitution({
      DatasetId: values.DatasetId,
      OriginalInputId: values.OriginalInputId,
      OriginalPlantId: values.OriginalPlantId,
      ReplacementInputId: values.ReplacementInputId,
      ReplacementPlantId: values.ReplacementPlantId,
    });
    reset();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <FormattedMessage
          id="SUBSTITUTIONS.CREATE_TITLE"
          defaultMessage="Add substitution"
        />
      </DialogTitle>
      <DialogContent>
        <div className="flex flex-col gap-4 mt-2">
          <Controller
            name="DatasetId"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Autocomplete
                size="small"
                options={datasetOptions}
                value={datasetOptions.find(o => o.id === field.value) ?? null}
                onChange={(_, value) => field.onChange(value?.id ?? null)}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={
                      <FormattedMessage
                        id="SUBSTITUTIONS.DATASET"
                        defaultMessage="Dataset"
                      />
                    }
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            )}
          />

          <Controller
            name="SubstitutionType"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <FormControl>
                <FormLabel>
                  <FormattedMessage
                    id="SUBSTITUTIONS.TYPE_SELECTION"
                    defaultMessage="Substitution Type"
                  />
                </FormLabel>
                <RadioGroup
                  row
                  value={field.value ?? ""}
                  onChange={e => field.onChange(e.target.value)}>
                  <FormControlLabel
                    value="tree"
                    control={<Radio />}
                    label={
                      <FormattedMessage
                        id="SUBSTITUTIONS.TREE_TYPE"
                        defaultMessage="Tree"
                      />
                    }
                  />
                  <FormControlLabel
                    value="crop"
                    control={<Radio />}
                    label={
                      <FormattedMessage
                        id="SUBSTITUTIONS.CROP_TYPE"
                        defaultMessage="Crop"
                      />
                    }
                  />
                  <FormControlLabel
                    value="input"
                    control={<Radio />}
                    label={
                      <FormattedMessage
                        id="SUBSTITUTIONS.INPUT_TYPE"
                        defaultMessage="Input"
                      />
                    }
                  />
                </RadioGroup>
              </FormControl>
            )}
          />

          {(selectedType === "tree" || selectedType === "crop") && (
            <div className="grid grid-cols-2 gap-4">
              <Controller
                name="OriginalPlantId"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Autocomplete
                    size="small"
                    options={currentOptions}
                    value={
                      currentOptions.find(o => o.id === field.value) ?? null
                    }
                    onChange={(_, value) => field.onChange(value?.id ?? null)}
                    getOptionLabel={option => option.label}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={
                          selectedType === "tree" ? (
                            <FormattedMessage
                              id="SUBSTITUTIONS.ORIGINAL_TREE"
                              defaultMessage="Original Tree"
                            />
                          ) : (
                            <FormattedMessage
                              id="SUBSTITUTIONS.ORIGINAL_CROP"
                              defaultMessage="Original Crop"
                            />
                          )
                        }
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.uniqueKey}>
                        <span style={{ fontFamily: "monospace" }}>
                          {option.label}
                        </span>
                      </li>
                    )}
                  />
                )}
              />
              <Controller
                name="ReplacementPlantId"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Autocomplete
                    size="small"
                    options={currentOptions}
                    value={
                      currentOptions.find(o => o.id === field.value) ?? null
                    }
                    onChange={(_, value) => field.onChange(value?.id ?? null)}
                    getOptionLabel={option => option.label}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={
                          selectedType === "tree" ? (
                            <FormattedMessage
                              id="SUBSTITUTIONS.REPLACEMENT_TREE"
                              defaultMessage="Replacement Tree"
                            />
                          ) : (
                            <FormattedMessage
                              id="SUBSTITUTIONS.REPLACEMENT_CROP"
                              defaultMessage="Replacement Crop"
                            />
                          )
                        }
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.uniqueKey}>
                        <span style={{ fontFamily: "monospace" }}>
                          {option.label}
                        </span>
                      </li>
                    )}
                  />
                )}
              />
            </div>
          )}

          {selectedType === "input" && (
            <div className="grid grid-cols-2 gap-4">
              <Controller
                name="OriginalInputId"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Autocomplete
                    size="small"
                    options={currentOptions}
                    value={
                      currentOptions.find(o => o.id === field.value) ?? null
                    }
                    onChange={(_, value) => field.onChange(value?.id ?? null)}
                    getOptionLabel={option => option.label}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={
                          <FormattedMessage
                            id="SUBSTITUTIONS.ORIGINAL_INPUT"
                            defaultMessage="Original Input"
                          />
                        }
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.uniqueKey}>
                        <span style={{ fontFamily: "monospace" }}>
                          {option.label}
                        </span>
                      </li>
                    )}
                  />
                )}
              />
              <Controller
                name="ReplacementInputId"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Autocomplete
                    size="small"
                    options={currentOptions}
                    value={
                      currentOptions.find(o => o.id === field.value) ?? null
                    }
                    onChange={(_, value) => field.onChange(value?.id ?? null)}
                    getOptionLabel={option => option.label}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={
                          <FormattedMessage
                            id="SUBSTITUTIONS.REPLACEMENT_INPUT"
                            defaultMessage="Replacement Input"
                          />
                        }
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.uniqueKey}>
                        <span style={{ fontFamily: "monospace" }}>
                          {option.label}
                        </span>
                      </li>
                    )}
                  />
                )}
              />
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          <FormattedMessage id="APP.CANCEL" defaultMessage="Cancel" />
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={isPending}>
          <FormattedMessage id="APP.ADD" defaultMessage="Add" />
        </Button>
      </DialogActions>
    </Dialog>
  );
}
